import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { map, Observable, take, tap } from 'rxjs';

import { SelectedSubproject, UserSession } from '@shared/models';
import { ConfirmDialogComponent } from '@shared/components';
import { UserDetailsFacade } from '@shared/state/facades';
import { Constants, getStaticMessage } from '@shared/utils';
import { RefreshType, StaticMessage } from '@shared/enums';
import { DataRefreshService } from '@shared/services';

@UntilDestroy()
@Injectable()
export class UserSessionChangeService {
  constructor(
    private userDetailsFacade: UserDetailsFacade,
    private dataRefreshService: DataRefreshService,
    private dialog: MatDialog
  ) {}

  isUserSessionChange(
    oldSelectedSubproject: SelectedSubproject,
    newSelectedSubproject: SelectedSubproject
  ): boolean {
    if (!oldSelectedSubproject || !newSelectedSubproject) {
      return false;
    }

    if (this.isSubprojectChange(oldSelectedSubproject, newSelectedSubproject)) {
      return true;
    }

    const oldSelectedSubprojectOnly = JSON.stringify({
      ...oldSelectedSubproject,
      data: null,
    });
    const newSelectedSubprojectOnly = JSON.stringify({
      ...newSelectedSubproject,
      data: null,
    });

    return oldSelectedSubprojectOnly !== newSelectedSubprojectOnly;
  }

  isSubprojectChange(
    oldSelectedSubproject: SelectedSubproject,
    newSelectedSubproject: SelectedSubproject
  ): boolean {
    if (!oldSelectedSubproject || !newSelectedSubproject) {
      return false;
    }

    // subproject change on the top and project setup level
    return (
      oldSelectedSubproject?.id !== newSelectedSubproject?.id ||
      oldSelectedSubproject?.id !==
        newSelectedSubproject?.data?.subprojectConfiguration?.subprojectId ||
      oldSelectedSubproject?.data?.subprojectConfiguration?.subprojectId !==
        newSelectedSubproject?.data?.subprojectConfiguration?.subprojectId
    );
  }

  checkForUserSessionChanges(
    data: UserSession,
    title: string,
    message: string,
    triggerReload = false
  ): void {
    this.userSessionChanged(data)
      .pipe(
        untilDestroyed(this),
        tap((userSessionChanged) => {
          if (userSessionChanged) {
            this.userDetailsFacade.updateUserSession(data);

            if (triggerReload) {
              const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
                ...Constants.DialogOptions,
                data: {
                  title,
                  message,
                  buttonLabel: getStaticMessage(StaticMessage.OkButton),
                  hideCancelButton: true,
                },
              });

              confirmDialog
                .afterClosed()
                .pipe(
                  untilDestroyed(this),
                  tap(() => {
                    this.dataRefreshService.setRefreshNeeded(
                      RefreshType.UserSessionChange
                    );
                  })
                )
                .subscribe();
            }
          }
        })
      )
      .subscribe();
  }

  //#region Private methods

  private userSessionChanged(data: UserSession): Observable<boolean> {
    return this.userDetailsFacade.query.userDetails$.pipe(
      take(1),
      map((userDetails) => {
        return (
          !this.equals(data.subprojects, userDetails.subprojects) ||
          !this.equals(data.clientsList, userDetails.clientsList) ||
          !this.equals(data.projects, userDetails.projects) ||
          data.settingsRight !== userDetails.settingsRight ||
          data.userAdminRight !== userDetails.userAdminRight
        );
      })
    );
  }

  private equals(data1: any, data2: any): boolean {
    const sanitizeDataFunct = (key: string, val: any) => {
      return key !== 'canBeDeleted' && key !== 'showTab' && key !== '$$hashKey'
        ? val
        : undefined;
    };

    // exclude the canBeDeleted project setup property and $$hashKey, because it is not relevant for the comparison
    const data1String = JSON.stringify(data1, sanitizeDataFunct);
    const data2String = JSON.stringify(data2, sanitizeDataFunct);
    return data1String === data2String;
  }

  //#endregion
}
